export function setDocumentColor(value: string) {
  const body = document.body;
  const classList = body.classList;

  // Remove any existing bg- classes
  const existingBgClasses = Array.from(classList).filter((className) =>
    className.startsWith('bg-'),
  );

  if (existingBgClasses.length > 0) {
    classList.remove(...existingBgClasses);
  }

  // Add the new background class
  classList.add(`bg-${value}`);
}

export function getRootContainer(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  return document.getElementById('root');
}

export function setDocumentMinHeight(height: string): void {
  if (typeof document !== 'undefined') {
    document.body.style.minHeight = height;
  }
}

export function clearDocumentMinHeight(): void {
  if (typeof document !== 'undefined') {
    document.body.style.minHeight = '';
  }
}

export function setDocumentTitle(title: string): void {
  if (typeof document !== 'undefined') {
    document.title = title;
  }
}

export function setDocumentFavicon(faviconUrl: string): void {
  if (typeof document === 'undefined') return;
  const link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
  if (link) {
    link.href = faviconUrl;
  } else {
    const newLink = document.createElement('link');
    newLink.rel = 'icon';
    newLink.href = faviconUrl;
    document.head.appendChild(newLink);
  }
}

export function loadFonts(
  fonts: {
    family: string;
    url: string;
    weight?: string | number;
    style?: string;
    crossOrigin?: string;
  }[],
): void {
  if (typeof document === 'undefined') return;

  const styleElement = document.createElement('style');
  let fontFaceRules = '';
  const preconnectDomains = new Set<string>();
  const stylesheetLinks: HTMLLinkElement[] = [];

  fonts.forEach((font) => {
    // If it's a direct font file (ends with a font extension) or has weight/style specified
    // we should create a @font-face rule.
    // Otherwise, we assume it's a stylesheet link (like Google Fonts).
    const isDirectFontFile =
      /\.(woff|woff2|ttf|otf|eot)(\?.*)?$/i.test(font.url) ||
      font.weight ||
      font.style;

    if (isDirectFontFile) {
      fontFaceRules += `
        @font-face {
          font-family: '${font.family}';
          src: url('${font.url}') format('${getFontFormat(font.url)}');
          ${font.weight ? `font-weight: ${font.weight};` : ''}
          ${font.style ? `font-style: ${font.style};` : ''}
          font-display: swap;
        }
      `;
    } else {
      // Add preconnect for external Google font stylesheets
      const url = new URL(font.url);
      if (url.hostname.includes('googleapis.com')) {
        preconnectDomains.add('https://fonts.googleapis.com');
        preconnectDomains.add('https://fonts.gstatic.com');
      }

      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = font.url;
      if (font.crossOrigin) {
        link.crossOrigin = font.crossOrigin;
      }
      stylesheetLinks.push(link);
    }
  });

  // Add preconnect links first
  preconnectDomains.forEach((domain) => {
    const preconnect = document.createElement('link');
    preconnect.rel = 'preconnect';
    preconnect.href = domain;
    if (domain.includes('gstatic.com')) {
      preconnect.crossOrigin = 'anonymous';
    }
    document.head.appendChild(preconnect);
  });

  // Then add stylesheet links
  stylesheetLinks.forEach((link) => {
    document.head.appendChild(link);
  });

  if (fontFaceRules) {
    styleElement.textContent = fontFaceRules;
    document.head.appendChild(styleElement);
  }
}

function getFontFormat(url: string): string {
  const extension = url.split('.').pop()?.split('?')[0]?.toLowerCase();
  switch (extension) {
    case 'woff2':
      return 'woff2';
    case 'woff':
      return 'woff';
    case 'ttf':
      return 'truetype';
    case 'otf':
      return 'opentype';
    case 'eot':
      return 'embedded-opentype';
    default:
      return 'woff2';
  }
}

export function loadGoogleTagManager(config: {
  scriptUrl: string;
  iframeUrl: string;
}): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  // Initialize dataLayer
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  // Load GTM script
  const script = document.createElement('script');
  script.async = true;
  script.src = config.scriptUrl;

  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }

  // Add noscript iframe for GTM
  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = config.iframeUrl;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
}

export function loadKlaviyo(config: { scriptUrl: string }): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;

  const script = document.createElement('script');
  script.async = true;
  script.type = 'text/javascript';
  script.src = config.scriptUrl;

  document.body.appendChild(script);
}

export function setRootCSSProperty(property: string, value: string): void {
  if (typeof document !== 'undefined') {
    document.documentElement.style.setProperty(property, value);
  }
}
